<template>
    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
        <div class="flex items-center justify-between px-6 py-3 border-b border-gray-300">
            <h3 class="text-lg text-gray-700 font-bold">Consumables & Pharmacy</h3>
            <USelectMenu v-if="canWrite" v-model="adding" :options="itemOptions" value-attribute="id" option-attribute="label" searchable
                searchable-placeholder="Search this post's stock..." :popper="{ placement: 'bottom-end' }" :ui-menu="{ width: 'w-80' }"
                @change="(v: string) => add(v)">
                <UButton label="Add" icon="i-ph-plus-bold" color="black" size="sm" />
                <template #option-empty="{ query }">
                    No stock matches "{{ query }}". Restock it from the Inventory tab.
                </template>
                <template #empty>
                    No stock at this post. Restock from the Inventory tab.
                </template>
            </USelectMenu>
        </div>

        <div v-if="!rows.length" class="flex flex-col items-center justify-center space-y-1 p-6">
            <UIcon name="i-ph-pill-duotone" class="size-5 text-gray-500" />
            <span class="text-[13px] italic text-gray-500">No consumables or medications.</span>
        </div>

        <div v-else class="divide-y divide-dashed divide-gray-300">
            <div v-for="row in rows" :key="row.id" class="space-y-4 px-6 py-5">
                <div class="flex items-center justify-between gap-4">
                    <div class="flex min-w-0 items-center gap-2">
                        <p class="truncate font-medium text-gray-800">
                            {{ row.lot?.item?.name }}<span v-if="row.lot?.item?.specification" class="text-gray-500"> · {{ row.lot.item.specification }}</span>
                        </p>
                        <UBadge v-if="isMedication(row)" color="gray" variant="subtle" size="xs" label="Medication" />
                    </div>
                    <UDropdown v-if="canWrite" :items="rowActions(row)" :popper="{ placement: 'bottom-end' }">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </div>

                <div class="grid grid-cols-6 gap-4">
                    <UFormGroup label="Lot" class="col-span-4">
                        <USelectMenu v-model="row.lot_id" :options="lotOptions(row)" value-attribute="id" option-attribute="label"
                            :disabled="!canWrite" @change="(v: string) => update(row, { lot_id: v })" />
                    </UFormGroup>
                    <UFormGroup label="Quantity" class="col-span-2">
                        <div class="flex items-center gap-1">
                            <UButton color="white" icon="i-ph-minus-bold" :disabled="!canWrite || row.qty <= 1" @click="setQty(row, row.qty - 1)" />
                            <span class="min-w-[2.5rem] text-center font-mono font-semibold text-gray-800">{{ formatQty(row.qty) }}</span>
                            <UButton color="white" icon="i-ph-plus-bold" :disabled="!canWrite" @click="setQty(row, row.qty + 1)" />
                            <span class="ml-1 truncate text-sm text-gray-500">{{ row.lot?.item?.unit }}</span>
                        </div>
                    </UFormGroup>
                    <template v-if="isMedication(row)">
                        <UFormGroup label="Dose" class="col-span-2">
                            <UInput v-model="row.dose" placeholder="e.g. 1 mg" :disabled="!canWrite" @blur="saveText(row, 'dose')" />
                        </UFormGroup>
                        <UFormGroup label="Route" class="col-span-2">
                            <USelectMenu v-model="row.route" :options="ROUTE_OPTIONS" value-attribute="value" option-attribute="label"
                                placeholder="Select..." :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => update(row, { route: v })">
                                <template v-if="canWrite && row.route" #trailing>
                                    <button type="button" class="flex items-center justify-center" @click.stop="update(row, { route: null })">
                                        <UIcon name="i-ph-x-bold" class="size-3" />
                                    </button>
                                </template>
                            </USelectMenu>
                        </UFormGroup>
                        <UFormGroup label="Status" class="col-span-2">
                            <USelectMenu v-model="row.status" :options="TREATMENT_STATUS_OPTIONS" value-attribute="value" option-attribute="label"
                                placeholder="Select..." :disabled="!canWrite" @change="(v: string) => update(row, { status: v })" />
                        </UFormGroup>
                    </template>
                    <UFormGroup label="Notes" class="col-span-6">
                        <UInput v-model="row.notes" :disabled="!canWrite" @blur="saveText(row, 'notes')" />
                    </UFormGroup>
                </div>
            </div>
        </div>

        <OverlayFieldPostRemoveRow v-model:open="removal.open" title="Remove Item"
            description="Are you sure you want to remove this item? The quantity goes back into the post's stock." :loading="removal.loading"
            @confirm="remove()" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { InventoryItemKind, ROUTE_OPTIONS, TREATMENT_STATUS_OPTIONS, TreatmentStatus, type InterventionUsage, type StockLot } from '~/types/fieldPost'
import { defaultLotFor, formatQty, todayIso } from '~/utils/fieldPosts'

const props = defineProps<{
    interventionId: string
    postId: string
    canWrite: boolean
}>()

const client = useSupabaseClient()
const auth = useAuthStore()
const toast = useToast()

const CLEARABLE_UI = { icon: { trailing: { pointer: 'pointer-events-auto' } } }

const rows = ref<InterventionUsage[]>([])
const lots = ref<StockLot[]>([])
const adding = ref<string | null>(null)
const removal = reactive({ open: false, loading: false, row: null as InterventionUsage | null })
const lastSaved = new Map<string, string | null>()

function rememberSaved() {
    for (const r of rows.value) {
        lastSaved.set(`${r.id}:dose`, r.dose?.trim() || null)
        lastSaved.set(`${r.id}:notes`, r.notes?.trim() || null)
    }
}

const itemOptions = computed(() => {
    const seen = new Map<string, { id: string; label: string }>()
    for (const lot of lots.value) {
        if (!lot.item || lot.item.is_archived || seen.has(lot.item_id)) continue
        seen.set(lot.item_id, { id: lot.item_id, label: [lot.item.name, lot.item.specification].filter(Boolean).join(' · ') })
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label))
})

function isMedication(row: InterventionUsage) {
    return row.lot?.item?.kind === InventoryItemKind.medication
}

function lotOptions(row: InterventionUsage) {
    return lots.value
        .filter(l => l.item_id === row.lot?.item_id)
        .map(l => ({
            id: l.id,
            label: [
                `Lot ${l.lot_number || '–'}`,
                `Exp. ${l.expires_on ? dayjs(l.expires_on).format('MMM YYYY') : 'none'}`,
                l.storage?.name,
                `${formatQty(l.qty_on_hand)} left`,
            ].filter(Boolean).join(' · '),
        }))
}

function rowActions(row: InterventionUsage) {
    return [[{
        label: 'Remove',
        icon: 'i-ph-trash-fill',
        iconClass: 'text-red-400',
        click: () => Object.assign(removal, { open: true, row }),
    }]]
}

function notSaved(title: string, error: { message: string }, retry?: () => void) {
    toast.add({
        title,
        description: error.message,
        color: 'red',
        icon: 'i-ph-x-circle-duotone',
        actions: retry ? [{ label: 'Retry', color: 'white', click: retry }] : undefined,
    })
}

onMounted(() => fetchAll())

let lotsRequest = 0

async function fetchLots() {
    const id = ++lotsRequest
    const { data } = await client.from('stock_lots').select('*, item:inventory_items (*), storage:field_post_storages (id, name)').eq('post_id', props.postId)
    if (id === lotsRequest) lots.value = (data ?? []) as StockLot[]
}

async function fetchAll() {
    const [usagesRes] = await Promise.all([
        client.from('intervention_usages').select('*, lot:stock_lots (*, item:inventory_items (*))').eq('intervention_id', props.interventionId).order('used_at'),
        fetchLots(),
    ])
    rows.value = (usagesRes.data ?? []) as InterventionUsage[]
    rememberSaved()
}

async function add(itemId: string) {
    adding.value = null
    const lot = defaultLotFor(lots.value, itemId, todayIso())
    if (!lot) return

    const { error } = await client.from('intervention_usages').insert({
        intervention_id: props.interventionId,
        lot_id: lot.id,
        qty: 1,
        used_by: auth.user?.id,
        status: lot.item?.kind === InventoryItemKind.medication ? TreatmentStatus.administered : null,
    } as never)
    if (error) notSaved('Could not add the item.', error)
    await fetchAll()
}

const rowQueues = new Map<string, Promise<void>>()

function update(row: InterventionUsage, patch: Record<string, unknown>) {
    const run = (rowQueues.get(row.id) ?? Promise.resolve()).then(() => applyUpdate(row, patch))
    rowQueues.set(row.id, run)
    run.finally(() => { if (rowQueues.get(row.id) === run) rowQueues.delete(row.id) })
    return run
}

async function applyUpdate(row: InterventionUsage, patch: Record<string, unknown>) {
    const { error } = await client.from('intervention_usages').update(patch as never).eq('id', row.id)
    if (error) {
        notSaved('Item not saved.', error)
        await fetchAll()
        return
    }
    Object.assign(row, patch)
    if ('lot_id' in patch) row.lot = lots.value.find(l => l.id === patch.lot_id) ?? row.lot
    if ('qty' in patch || 'lot_id' in patch) await fetchLots()
}

async function saveText(row: InterventionUsage, field: 'dose' | 'notes', force = false) {
    const key = `${row.id}:${field}`
    const value = row[field]?.trim() || null
    if (!force && value === (lastSaved.get(key) ?? null)) return
    const { error } = await client.from('intervention_usages').update({ [field]: value } as never).eq('id', row.id)
    if (error) {
        notSaved(field === 'dose' ? 'Dose not saved.' : 'Notes not saved.', error, () => saveText(row, field, true))
        return
    }
    lastSaved.set(key, value)
}

function setQty(row: InterventionUsage, qty: number) {
    if (qty < 1) return
    row.qty = qty
    update(row, { qty })
}

async function remove() {
    if (!removal.row) return
    removal.loading = true
    const { error } = await client.from('intervention_usages').delete().eq('id', removal.row.id)
    removal.loading = false
    if (error) notSaved('Could not remove the item.', error)
    else removal.open = false
    await fetchAll()
}
</script>
